import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save,
  X,
  Upload,
  Trash2,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  Gamepad2
} from 'lucide-react';
import { booksService, digitalOceanService } from '../services/api';

// Registry of games available in suavemente-front/public/games/.
// Add an entry here when boss ships a new mini-game.
const AVAILABLE_GAMES = [
  { slug: 'sena-pacman', title: 'Sena Pacman' },
];
const DEFAULT_GAME_SLUG = AVAILABLE_GAMES[0].slug;

const AdminBookEdit = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [book, setBook] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState({
    cover: false,
    pdf: false
  });
  
  // Fetch book details
  useEffect(() => {
    fetchBookDetails();
  }, [bookId]);
  
  const fetchBookDetails = async () => {
    try {
      setLoading(true);
      const data = await booksService.getBook(bookId);
      setBook(data);
      
      // Initialize form data
      setFormData({
        title: data.title || '',
        description: data.description || '',
        author_bio: data.author_bio || '',
        genre: data.genre?.id || '',
        price: data.price || '',
        age_rating: data.age_rating || 'all',
        sample_pages_count: data.sample_pages_count || 5,
        status: data.status || 'approved',
        game_slug: data.game_slug || null
      });
    } catch (error) {
      console.error('Error fetching book:', error);
      alert('Failed to load book details');
      navigate('/admin/books');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleFileUpload = async (type, file) => {
    try {
      setUploading(prev => ({ ...prev, [type]: true }));
      
      let uploadData;
      if (type === 'cover') {
        uploadData = await digitalOceanService.generateBookCoverUploadURL({
          filename: file.name
        });
      } else {
        uploadData = await digitalOceanService.generateBookPDFUploadURL({
          filename: file.name
        });
      }
      
      if (!uploadData.success) {
        throw new Error(uploadData.error);
      }
      
      // Upload to DO Spaces
      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );
      
      // Replace the file on the book
      const result = await booksService.replaceBookFile(
        bookId,
        type,
        uploadData.public_url
      );
      
      if (result.success) {
        alert(`${type === 'cover' ? 'Cover image' : 'PDF'} updated successfully!`);
        fetchBookDetails(); // Refresh book data
      } else {
        throw new Error(result.error);
      }
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(`Failed to upload ${type}: ${error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price) {
      alert('Title and price are required');
      return;
    }
    
    try {
      setSaving(true);
      const result = await booksService.updateBook(bookId, formData);
      
      if (result.success) {
        alert('Book updated successfully!');
        navigate(`/book/${bookId}`);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error updating book:', error);
      alert(`Failed to update book: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${book?.title}"? This will remove all files from storage and cannot be undone.`)) {
      return;
    }
    
    try {
      const result = await booksService.deleteBook(bookId);
      
      if (result.success) {
        alert(`Book "${result.deleted_book.title}" deleted successfully`);
        navigate('/admin/books');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      alert(`Failed to delete book: ${error.message}`);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
      </div>
    );
  }
  
  if (!book) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Book not found</h3>
        <button
          onClick={() => navigate('/admin/books')}
          className="mt-4 px-4 py-2 bg-[#1d1d1d] text-white rounded-lg"
        >
          Back to Books
        </button>
      </div>
    );
  }
  
  return (
    <div className="min-h-full bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Book</h1>
            <p className="text-gray-600 font-medium">Update book details and files</p>
          </div>
          <button
            onClick={() => navigate(`/book/${bookId}`)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            View Book
          </button>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Book Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Book Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows="4"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author Bio
                  </label>
                  <textarea
                    name="author_bio"
                    rows="3"
                    value={formData.author_bio}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] resize-none"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Status</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (GHS) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sample Pages
                    </label>
                    <input
                      type="number"
                      name="sample_pages_count"
                      min="1"
                      max="50"
                      value={formData.sample_pages_count}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d]"
                    >
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="draft">Draft</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age Rating
                  </label>
                  <select
                    name="age_rating"
                    value={formData.age_rating}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d]"
                  >
                    <option value="all">All Ages</option>
                    <option value="teen">Teen</option>
                    <option value="adult">Adult</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* File Management */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">File Management</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Cover Image */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <ImageIcon className="h-5 w-5 mr-2" />
                      Cover Image
                    </h4>
                    {book.cover_image_url && (
                      <a
                        href={book.cover_image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        View Current
                      </a>
                    )}
                  </div>
                  
                  {book.cover_image_url && (
                    <div className="mb-4">
                      <img
                        src={book.cover_image_url}
                        alt="Current cover"
                        className="h-40 w-32 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload New Cover
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleFileUpload('cover', file);
                      }}
                      disabled={uploading.cover}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      JPG, PNG, or WebP. Max 5MB. Recommended: 328×500px
                    </p>
                  </div>
                </div>
                
                {/* PDF File */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <FileText className="h-5 w-5 mr-2" />
                      PDF File
                    </h4>
                    {book.full_pdf_url && (
                      <a
                        href={book.full_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        View Current
                      </a>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">Current PDF</p>
                          <p className="text-sm text-gray-600">
                            {book.total_pages || 0} pages extracted
                          </p>
                        </div>
                        {book.total_pages > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('Reprocess PDF to extract pages?')) {
                                booksService.extractBookPages(bookId)
                                  .then(() => {
                                    alert('Page extraction started');
                                    fetchBookDetails();
                                  })
                                  .catch(err => alert('Error: ' + err.message));
                              }
                            }}
                            className="p-2 text-gray-600 hover:text-gray-900"
                            title="Reprocess PDF"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload New PDF
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleFileUpload('pdf', file);
                      }}
                      disabled={uploading.pdf}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      PDF only. Max 50MB. Pages will be auto-extracted.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mini-Game */}
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Gamepad2 className="h-5 w-5 mr-2 text-[#ff9702]" />
                Mini-Game
              </h3>

              <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6">
                <div className="flex items-start justify-between gap-6">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">Enable mini-game on the book page</span>
                      {formData.game_slug && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-green-100 text-green-700">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      When enabled, readers see a <b>Play &amp; Win Free</b> button next to Buy Book.
                      Beating every level of the game unlocks the book for free — no payment required.
                    </p>
                    {formData.game_slug && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                        <span>Active game:</span>
                        <code className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-mono">{formData.game_slug}</code>
                        {!AVAILABLE_GAMES.some(g => g.slug === formData.game_slug) && (
                          <span className="text-amber-600 font-medium">(custom slug — not in the built-in list)</span>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!formData.game_slug}
                    onClick={() =>
                      setFormData(prev => ({
                        ...prev,
                        game_slug: prev.game_slug ? null : DEFAULT_GAME_SLUG,
                      }))
                    }
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#ff9702] focus:ring-offset-2 ${
                      formData.game_slug ? 'bg-[#ff9702]' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        formData.game_slug ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {AVAILABLE_GAMES.length > 1 && formData.game_slug && (
                  <div className="mt-5 pt-5 border-t border-gray-200">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Which game?
                    </label>
                    <select
                      name="game_slug"
                      value={formData.game_slug}
                      onChange={handleChange}
                      className="w-full sm:w-64 px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#ff9702] text-sm"
                    >
                      {AVAILABLE_GAMES.map(g => (
                        <option key={g.slug} value={g.slug}>{g.title}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t border-gray-200 pt-8 flex justify-between">
              <button
                type="button"
                onClick={handleDelete}
                className="px-6 py-3 border border-red-300 text-red-700 rounded-lg font-semibold hover:bg-red-50 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Book
              </button>
              
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 flex items-center"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminBookEdit;